import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { listGroups } from '../../lib/api/groups'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { usePerson } from '../../contexts/PersonContext'
import { NewGroupModal } from '../groups/NewGroupModal'
import { cn } from '../../lib/cn'
import { SectionLabel } from '../ui/SectionLabel'
import { SidebarAddButton } from './SidebarAddButton'

/**
 * "Groups" sidebar section. Consumes GET /community/groups (COTC_Groups fills the
 * stub in). Empty until then, showing the design's empty-state copy.
 */
export function GroupsSection() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { role } = usePerson()
  const [creating, setCreating] = useState(false)

  const { data: groups = [], isError, isLoading, refetch } = useQuery({
    queryKey: ['community', 'groups'],
    queryFn: listGroups,
    retry: false,
  })

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2 pl-[10px] pr-[6px]">
        <SectionLabel>Groups</SectionLabel>
        {role === 'parent' && <SidebarAddButton
          title="Start a group for a game"
          onClick={() => setCreating(true)}
        />}
      </div>
      {isError ? <div role="alert" className="px-[10px] text-[12px] text-ink-muted"><p>Groups could not be loaded.</p><button className="mt-2 underline" onClick={() => void refetch()}>Retry group list</button></div> : isLoading ? <p role="status" className="px-[10px] text-[12px] text-ink-muted">Loading groups…</p> : groups.length === 0 ? (
        <p className="px-[10px] pb-1 pt-[2px] text-[11px] leading-relaxed text-ink-fainter">
          No groups yet — groups are per-game. You join as yourself, and you can invite a circle so people you already trust can find you there.
        </p>
      ) : (
        groups.map((g) => {
          const active = pathname === `/groups/${g.id}`
          return (
            <button
              key={g.id}
              type="button"
              onClick={() => navigate(`/groups/${g.id}${g.role === 'Observer' ? '/observe' : ''}`)}
              className={cn(
                'flex w-full items-center gap-[9px] rounded-btn px-[10px] py-[9px] text-left text-[13px] font-medium transition-colors',
                active ? 'bg-paper-deep text-ink' : 'bg-transparent text-ink-soft hover:bg-paper-deep/50',
              )}
            >
              <span className="h-[9px] w-[9px] flex-none rounded-[2px]" style={{ background: g.dot }} />
              <span className="min-w-0 flex-1">
                <span className="block truncate">{g.name}</span>
                <span className="block truncate font-mono text-[10px] uppercase tracking-label text-ink-fainter">
                  {g.game} · {g.role === 'Observer' ? 'Guardian observation' : `${g.circleCount} circles · ${g.familyCount} fam`}
                </span>
              </span>
            </button>
          )
        })
      )}
      <Link to="/group-invitations" className="px-[10px] py-2 text-[12px] text-terra-deep">Group invitations</Link>
      {creating && role === 'parent' && <NewGroupModal open mode="create" onClose={() => setCreating(false)} onCreated={(group) => navigate(`/groups/${group.id}`)} />}
    </div>
  )
}
